import { Controller } from '@hotwired/stimulus'
import Text from '../../lib/text'
import Dom from '../../lib/dom'

export default class extends Controller {
  static targets = ['name', 'number', 'expiration', 'cvv', 'hash', 'token', 'error', 'submit']
  static values = { 'payment': Number, 'user': String, 'session': Number, 'debug': Boolean }

  log (...args) {
    if (this.debugValue) { console.log(...args) }
  }

  connect () {
    this.blockSensitiveParameters()
    this.log('[Tuna] Controller connected, has_email:', !!this.email)
  }

  generate (e) {
    this.log('[Tuna] Generate started for payment:', this.paymentValue)

    if (!this.validate()) {
      this.log('[Tuna] Validation failed')
      return
    }

    this.log('[Tuna] Validation passed, starting fetch session')
    const form = e.currentTarget.closest('form')

    Dom.fire(this.submitTarget, 'generating', null, { bubbles: true })

    fetch(`/api/tuna/${this.paymentValue}/new_session?email=${this.email}`)
      .then(async response => {
        this.log('[Tuna] Session fetch response status:', response.status)
        if (!response.ok) {
          let bodyText = ''
          try {
            bodyText = await response.text()
          } catch (e) {
            // Ignore body read errors; we'll still throw with status.
          }
          const error = new Error(`[Tuna] Session request failed with status ${response.status}`)
          error.status = response.status
          if (bodyText) error.body = bodyText
          throw error
        }
        return response.json()
      })
      .then(response => {
        this.log('[Tuna] Session response received, status:', response.status)
        if (response.status === -1) {
          this.log('[Tuna] Session error from API')
          this.handleError(null, response.message)
        } else {
          this.log('[Tuna] Session success, starting tokenization')
          const token = response.sessionId

          if (!window.Tuna) {
            console.error('[Tuna] Tuna library not loaded')
            throw new Error('Tuna library not loaded. Check if Tuna.js script is properly included.')
          }

          const tokenizer = Tuna(token).tokenizator()
          this.log('[Tuna] Tokenizer created successfully')

          tokenizer.generate(this.card)
            .then(response => {
              this.log('[Tuna] Tokenization success')
              this.submit(form, token, response.token)
            })
            .catch(error => {
              this.log('[Tuna] Tokenization error:', error?.message || 'unknown')
              this.handleError(error)
            })
        }
      }).catch(error => {
      this.log('[Tuna] Session fetch error:', error?.message || 'unknown')
      this.handleError(error)
    })
  }

  submit (form, token, hash) {
    this.tokenTarget.value = token
    this.hashTarget.value = hash
    this.blockSensitiveParameters()

    Dom.fire(form, 'submit', null, { bubbles: true })
  }

  handleError (error, message = null) {
    this.log('[Tuna] Error handler called, payment:', this.paymentValue, 'error:', error?.message || 'unknown')
    error = new Error(`Tuna card error at payment ${this.paymentValue}: ${error}`)
    this.log(error?.message)

    message ||= 'O pagamento foi recusado. Certifique-se que você está usando um cartão de crédito das bandeiras Visa, Mastercard ou Amex, e que os dados fornecidos estão corretos.'
    this.errorTarget.querySelector('p').textContent = message

    this.errorTarget.classList.remove('hidden')
    Dom.fire(this.submitTarget, 'rejected', null, { bubbles: true })

    fetch(`/api/tuna/${this.paymentValue}/errored?error=${error}`, { method: 'DELETE' })
  }

  validate () {
    this.log('[Tuna] Validation started')
    let valid = true
    valid = valid & this.validateField(this.nameTarget)
    valid = valid & this.validateField(this.numberTarget, 19)
    valid = valid & this.validateField(this.expirationTarget, 5)
    valid = valid & this.validateField(this.cvvTarget)
    this.log('[Tuna] Validation result:', valid)
    return valid
  }

  validateField (input, length) {
    let valid = true
    valid = valid && Text.isPresent(input.value)
    valid = valid && (!length || input.value.length === length)

    if (!valid) {
      input.classList.add('field_with_errors')
      input.nextElementSibling.classList.add('field_with_errors')
    }

    return valid
  }

  blockSensitiveParameters () {
    this.nameTarget.removeAttribute('name')
    this.numberTarget.removeAttribute('name')
    this.expirationTarget.removeAttribute('name')
    this.cvvTarget.removeAttribute('name')
  }

  toggleElement (element, force) {
    element.disabled = !force
    element.classList.toggle('disabled', !force)
  }

  get card () {
    return {
      cardHolderName: this.nameTarget.value,
      cardNumber: this.numberTarget.value.replace(/ /g, ''),
      expirationMonth: Number(this.expirationTarget.value.substring(0, 2)),
      expirationYear: Number(this.expirationTarget.value.substring(3, 5)) + 2000,
      cvv: this.cvvTarget.value,
      singleUse: true
    }
  }

  get email () {
    const input = document.querySelector('[name="payment[email]"]')
    return input ? input.value : ''
  }
}
